import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Timestamp } from "@travelix/core";

import { NavHeader } from "@/components/nav-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { TagInput } from "@/components/ui/tag-input";
import { useAuth } from "@/lib/auth-provider";
import { maybeShowTripCreatedInterstitial } from "@/lib/ads";
import { logAnalyticsEvent } from "@/lib/analytics";
import { parseYmdLocal } from "@/lib/trip-date-bounds";
import { createTripWithUser, unwrap, queryKeys, displayNameOf } from "@/lib/api";

// ─── Schema ──────────────────────────────────────────────────
const emailRegex = /^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/;

const createTripSchema = z
  .object({
    name: z.string().trim().min(1, "Give the trip a name"),
    destinations: z
      .array(z.string().min(1))
      .min(1, "Add at least one destination"),
    startDate: z.string().min(1, "Pick a start date"),
    endDate: z.string().min(1, "Pick an end date"),
    invites: z
      .array(z.string().regex(emailRegex, "Invalid email"))
      .default([]),
  })
  .refine(
    (v) => {
      const s = parseYmdLocal(v.startDate);
      const e = parseYmdLocal(v.endDate);
      return !s || !e || e.getTime() >= s.getTime();
    },
    { message: "End date can't be before start date", path: ["endDate"] },
  );

type CreateTripValues = z.infer<typeof createTripSchema>;

// ─── Route ───────────────────────────────────────────────────
export default function TripCreateRoute() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const form = useForm<CreateTripValues>({
    resolver: zodResolver(createTripSchema),
    defaultValues: {
      name: "",
      destinations: [],
      startDate: "",
      endDate: "",
      invites: [],
    },
  });

  const mutation = useMutation({
    mutationFn: async (values: CreateTripValues) => {
      if (!user?.email || !user.uid) throw new Error("Not signed in");
      // Parse `type="date"` values as LOCAL midnight so the persisted date
      // matches what the user picked (see parseYmdLocal for the timezone story).
      const startLocal = parseYmdLocal(values.startDate);
      const endLocal = parseYmdLocal(values.endDate);
      if (!startLocal || !endLocal) throw new Error("Pick valid dates");
      return unwrap(createTripWithUser({
        name: values.name.trim(),
        destinations: values.destinations,
        startDate: Timestamp.fromDate(startLocal),
        endDate: Timestamp.fromDate(endLocal),
        coverImage: null, // set later via Trip Settings → Change photo
        ownerUid: user.uid,
        ownerName: displayNameOf(user),
        ownerEmail: user.email,
        invitedEmails: values.invites,
      }));
    },
    onSuccess: ({ tripId, failedInvites }, values) => {
      qc.invalidateQueries({ queryKey: queryKeys.users() });
      if (failedInvites.length > 0) {
        toast.warning("Trip created, but some invites didn't go out.", {
          description: `Invite ${failedInvites.join(", ")} again from Trip settings → Members.`,
        });
      } else {
        toast.success("Trip created. Let's plan.");
      }
      logAnalyticsEvent("trip_created", {
        trip_id: tripId,
        destinations_count: values.destinations.length,
        invite_count: values.invites.length,
      });
      navigate(`/trip/${tripId}`, { replace: true });
      // Fire-and-forget: the interstitial lands on top of the freshly-navigated
      // trip detail. If it's disabled, capped, or fails, navigation is
      // unaffected. This is the primary revenue moment — the user just chose
      // to commit to a new trip and expects a brief pause here anyway.
      void maybeShowTripCreatedInterstitial();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Something went sideways.");
    },
  });

  return (
    <main className="min-h-full bg-bg pb-[calc(64px+var(--bottom-tabs-h,0px))]">
      <NavHeader title="New trip" showBack maxWidthClassName="max-w-md" />

      <form
        onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
        className="mx-auto flex max-w-md flex-col gap-5 px-5 pt-6"
        noValidate
      >
        <Field id="c-name" label="Trip name" error={form.formState.errors.name?.message}>
          <Input
            id="c-name"
            placeholder="Barcelona weekend"
            autoFocus
            invalid={!!form.formState.errors.name}
            {...form.register("name")}
          />
        </Field>

        <Field
          id="c-dest"
          label="Where to?"
          helper="Add one or a few stops. Press Enter or comma to add."
          error={form.formState.errors.destinations?.message}
        >
          <Controller
            control={form.control}
            name="destinations"
            render={({ field }) => (
              <TagInput
                id="c-dest"
                values={field.value}
                onChange={field.onChange}
                placeholder="Barcelona, Lisbon…"
              />
            )}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field id="c-start" label="Starts" error={form.formState.errors.startDate?.message}>
            <Input
              id="c-start"
              type="date"
              invalid={!!form.formState.errors.startDate}
              {...form.register("startDate")}
            />
          </Field>
          <Field id="c-end" label="Ends" error={form.formState.errors.endDate?.message}>
            <Input
              id="c-end"
              type="date"
              invalid={!!form.formState.errors.endDate}
              {...form.register("endDate")}
            />
          </Field>
        </div>

        <Field
          id="c-invite"
          label="Invite the crew (optional)"
          helper="They'll get an invite when we send emails (coming soon). For now, the trip appears in their invites list."
          error={
            Array.isArray(form.formState.errors.invites)
              ? form.formState.errors.invites.find(Boolean)?.message
              : form.formState.errors.invites?.message
          }
        >
          <Controller
            control={form.control}
            name="invites"
            render={({ field }) => (
              <TagInput
                id="c-invite"
                values={field.value}
                onChange={field.onChange}
                placeholder="marta@friends.com"
                inputMode="email"
                validate={(v) => (emailRegex.test(v) ? null : "That doesn't look like an email")}
              />
            )}
          />
        </Field>

        <div className="flex flex-col gap-2 pt-2">
          <Button type="submit" block disabled={mutation.isPending}>
            {mutation.isPending ? "Creating trip…" : "Create trip"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            block
            onClick={() => navigate(-1)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
        </div>
      </form>
    </main>
  );
}
